import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import {
  QrCode, Download, Printer, FileText, Building2, Loader2, ExternalLink, Eye,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SupportCardGenerator({ token, properties }) {
  const [selectedProperty, setSelectedProperty] = useState('');
  const [cardData, setCardData] = useState(null);
  const [sheetData, setSheetData] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('card'); // 'card' or 'sheet'
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [props, setProps] = useState(properties || []);
  const cardRef = useRef(null);
  const sheetRef = useRef(null);

  useEffect(() => {
    if (!properties?.length) {
      axios.get(`${BACKEND_URL}/api/properties?show_all=true`, { headers })
        .then(r => {
          const data = Array.isArray(r.data) ? r.data : r.data?.properties || [];
          setProps(data);
        })
        .catch(() => {});
    }
  }, [headers, properties]);

  const loadProperty = useCallback(async (propId) => {
    if (!propId) return;
    setSelectedProperty(propId);
    setLoading(true);
    try {
      const [cardRes, sheetRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/support/card/${propId}`, { headers }),
        axios.get(`${BACKEND_URL}/api/support/sheet/${propId}`, { headers }),
      ]);
      setCardData(cardRes.data);
      setSheetData(sheetRes.data);
      setSelectedPlatforms(new Set(sheetRes.data.platforms?.map(p => p.platform_id) || []));
    } catch { toast.error('Failed to load support data'); }
    finally { setLoading(false); }
  }, [headers]);

  const togglePlatform = (id) => {
    setSelectedPlatforms(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const fullSupportUrl = cardData ? `${window.location.origin}${cardData.support_url}` : '';

  const printCard = () => {
    const w = window.open('', '_blank', 'width=500,height=400');
    w.document.write(`<!DOCTYPE html><html><head><style>
      @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600;700&display=swap');
      body { margin: 0; font-family: 'Space Grotesk', sans-serif; display: flex; align-items: center; justify-content: center; min-height: 100vh; background: #f8fafc; }
      .card { width: 4in; height: 2.5in; border: 2px solid #e2e8f0; border-radius: 12px; padding: 20px; background: white; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
      .brand { font-size: 10px; text-transform: uppercase; letter-spacing: 2px; color: #0055FF; font-weight: 600; margin-bottom: 4px; }
      .name { font-size: 16px; font-weight: 700; color: #0f172a; margin-bottom: 12px; }
      .qr { width: 100px; height: 100px; border: 1px solid #e2e8f0; border-radius: 8px; margin-bottom: 8px; }
      .hint { font-size: 9px; color: #94a3b8; }
      @media print { body { background: white; } .card { border: 1px solid #ccc; box-shadow: none; } }
    </style></head><body>
      <div class="card">
        <div class="brand">LodgiFlow</div>
        <div class="name">${cardData?.property?.name || ''}</div>
        <img class="qr" src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(fullSupportUrl)}" />
        <div class="hint">Scan for tech support contacts</div>
      </div>
      <script>setTimeout(()=>window.print(),500)</script>
    </body></html>`);
    w.document.close();
  };

  const printSheet = () => {
    const filtered = sheetData?.platforms?.filter(p => selectedPlatforms.has(p.platform_id)) || [];
    const rows = filtered.map(p => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;font-weight:600;font-size:11px;">${p.platform_name}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;font-size:11px;">${p.product_name || p.vendor_name || '-'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;font-size:11px;">${p.support_contact_name || p.platform_admin || '-'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;font-size:11px;">${p.support_contact_email || '-'}<br/>${p.support_contact_phone || ''}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;font-size:11px;">${p.support_url ? `<a href="${p.support_url}">Link</a>` : '-'}</td>
      </tr>
    `).join('');

    const w = window.open('', '_blank', 'width=900,height=700');
    w.document.write(`<!DOCTYPE html><html><head><style>
      @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600;700&display=swap');
      body { margin: 0; padding: 24px; font-family: 'Space Grotesk', -apple-system, sans-serif; color: #0f172a; font-size: 12px; }
      h1 { font-size: 18px; margin: 0; } h2 { font-size: 12px; color: #64748b; margin: 4px 0 16px; font-weight: 400; }
      table { width: 100%; border-collapse: collapse; }
      th { padding: 8px; background: #f1f5f9; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #475569; text-align: left; border-bottom: 2px solid #e2e8f0; }
      .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
      .qr-area { text-align: right; }
      .qr-area img { width: 64px; height: 64px; }
      .qr-area p { font-size: 8px; color: #94a3b8; margin: 2px 0 0; }
      .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #e2e8f0; font-size: 9px; color: #94a3b8; display: flex; justify-content: space-between; }
      @media print { body { padding: 16px; } }
    </style></head><body>
      <div class="header">
        <div><h1>${sheetData?.property?.name || ''}</h1><h2>Technology Support Reference — ${filtered.length} Platforms</h2></div>
        <div class="qr-area"><img src="https://api.qrserver.com/v1/create-qr-code/?size=128x128&data=${encodeURIComponent(fullSupportUrl)}" /><p>Scan for live info</p></div>
      </div>
      <table><thead><tr><th>Platform</th><th>Product / Vendor</th><th>Support Contact</th><th>Email / Phone</th><th>Portal</th></tr></thead><tbody>${rows}</tbody></table>
      <div class="footer"><span>Generated by LodgiFlow</span><span>${new Date().toLocaleDateString()}</span></div>
      <script>setTimeout(()=>window.print(),500)</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <div className="flex flex-col md:flex-row h-full pb-mobile-nav" data-testid="support-card-generator">
      {/* Property List */}
      <div className="md:w-64 border-b md:border-b-0 md:border-r bg-muted/20 flex flex-col shrink-0">
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <QrCode className="h-4 w-4" /> Support Cards
          </h3>
          <p className="text-[10px] text-muted-foreground mt-1">Generate QR cards & support sheets</p>
        </div>
        {!selectedProperty ? (
          <ScrollArea className="flex-1 max-h-[40vh] md:max-h-none">
            <div className="p-2 space-y-1">
              {props.filter(p => p.id !== 'corporate').map(p => (
                <button key={p.id} onClick={() => loadProperty(p.id)} data-testid={`support-prop-${p.id}`}
                  className={`w-full text-left p-2.5 rounded-lg transition-colors text-sm ${
                    selectedProperty === p.id ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted/60'
                  }`}>
                  <span className="font-medium truncate block">{p.name}</span>
                </button>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="p-2 md:flex-1">
            <button onClick={() => { setSelectedProperty(''); setCardData(null); setSheetData(null); }}
              className="text-xs text-primary flex items-center gap-1 p-2">
              <span className="rotate-180">→</span> Back to properties
            </button>
            <ScrollArea className="hidden md:block flex-1">
              <div className="space-y-1">
                {props.filter(p => p.id !== 'corporate').map(p => (
                  <button key={p.id} onClick={() => loadProperty(p.id)} data-testid={`support-prop-${p.id}`}
                    className={`w-full text-left p-2.5 rounded-lg transition-colors text-sm ${
                      selectedProperty === p.id ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted/60'
                    }`}>
                    <span className="font-medium truncate block">{p.name}</span>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : cardData ? (
          <div className="p-6 space-y-6 max-w-3xl">
            {/* Mode tabs */}
            <div className="flex gap-1 bg-muted/60 rounded-lg p-0.5 w-fit">
              {[
                { id: 'card', label: 'QR Support Card', icon: QrCode },
                { id: 'sheet', label: 'Support Sheet', icon: FileText },
              ].map(m => (
                <button key={m.id} onClick={() => setMode(m.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    mode === m.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}>
                  <m.icon className="h-3.5 w-3.5" /> {m.label}
                </button>
              ))}
            </div>

            {mode === 'card' ? (
              <div className="space-y-4">
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>QR Support Card</h2>
                <p className="text-sm text-muted-foreground">Print this card and place at the front desk or back office. Staff can scan the QR code with their phone to access support contacts for all technology platforms.</p>

                {/* Card Preview */}
                <div ref={cardRef} className="w-[384px] h-[240px] border-2 border-dashed rounded-xl p-5 flex flex-col items-center justify-center text-center bg-card">
                  <p className="text-[10px] uppercase tracking-[3px] text-blue-600 font-semibold mb-1">LodgiFlow</p>
                  <p className="text-base font-bold mb-3">{cardData.property.name}</p>
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(fullSupportUrl)}`} alt="QR Code" className="w-24 h-24 rounded-lg border mb-2" />
                  <p className="text-[9px] text-muted-foreground">Scan for tech support contacts</p>
                </div>

                <div className="flex gap-2">
                  <Button onClick={printCard} className="gap-1.5"><Printer className="h-4 w-4" /> Print Card</Button>
                  <Button variant="outline" onClick={() => window.open(fullSupportUrl, '_blank')} className="gap-1.5"><Eye className="h-4 w-4" /> Preview Support Page</Button>
                </div>
                <p className="text-xs text-muted-foreground">Support URL: <code className="bg-muted px-1.5 py-0.5 rounded text-[10px]">{fullSupportUrl}</code></p>
              </div>
            ) : (
              <div className="space-y-4">
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Printable Support Sheet</h2>
                <p className="text-sm text-muted-foreground">Select which platforms to include, then generate a formatted print-ready support reference sheet with QR code.</p>

                {/* Platform selector */}
                <div className="space-y-1 max-h-[350px] overflow-y-auto border rounded-lg p-2">
                  <div className="flex justify-between items-center p-2">
                    <span className="text-xs font-semibold">{selectedPlatforms.size} of {sheetData?.platforms?.length || 0} selected</span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" className="text-[10px] h-6" onClick={() => setSelectedPlatforms(new Set(sheetData?.platforms?.map(p => p.platform_id) || []))}>All</Button>
                      <Button variant="ghost" size="sm" className="text-[10px] h-6" onClick={() => setSelectedPlatforms(new Set())}>None</Button>
                    </div>
                  </div>
                  {sheetData?.platforms?.map(p => (
                    <label key={p.platform_id} className="flex items-center gap-2.5 p-2 rounded hover:bg-muted/40 cursor-pointer">
                      <Checkbox checked={selectedPlatforms.has(p.platform_id)} onCheckedChange={() => togglePlatform(p.platform_id)} />
                      <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.category_color }} />
                      <span className="text-sm">{p.platform_name}</span>
                      <span className="text-[10px] text-muted-foreground ml-auto">{p.product_name || p.vendor_name}</span>
                    </label>
                  ))}
                </div>

                <Button onClick={printSheet} disabled={selectedPlatforms.size === 0} className="gap-1.5">
                  <Printer className="h-4 w-4" /> Generate & Print Support Sheet ({selectedPlatforms.size} platforms)
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <QrCode className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Select a property to generate support materials</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
